import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { interviewResultsService } from '@/services/api';
import {
  CandidateList,
  InterviewResultDataList,
  CommonRequest,
} from '@/services/api/interviewResultsTypes';

export interface CandidateFilters {
  createdOn: string | null;
  atsScore: string | null;
  atsStatus: 'All' | 'Passed' | 'Failed';
}

interface InterviewResultsState {
  candidates: CandidateList[];
  interviewResultDataList: InterviewResultDataList[];
  candidateName: string;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    totalRecords: number;
  };
  searchQuery: string;
  filters: CandidateFilters;
}

const initialState: InterviewResultsState = {
  candidates: [],
  interviewResultDataList: [],
  candidateName: '',
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalRecords: 0,
  },
  searchQuery: '',
  filters: {
    createdOn: null,
    atsScore: null,
    atsStatus: 'All',
  },
};

export const fetchAllCandidates = createAsyncThunk(
  'interviewResults/fetchAllCandidates',
  async (request: CommonRequest, { rejectWithValue }) => {
    try {
      const response = await interviewResultsService.getAllCandidates(request);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch candidates'
      );
    }
  }
);

export const fetchInterviewResults = createAsyncThunk(
  'interviewResults/fetchInterviewResults',
  async (
    {
      candidateUid,
      interviewUid,
    }: { candidateUid: string; interviewUid?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await interviewResultsService.getInterviewResults(
        candidateUid,
        interviewUid
      );
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch interview results'
      );
    }
  }
);

const interviewResultsSlice = createSlice({
  name: 'interviewResults',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.pagination.page = 1; // Reset to page 1 on search
    },
    setFilters: (state, action: PayloadAction<Partial<CandidateFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to page 1 on filter change
    },
    clearFilters: state => {
      state.searchQuery = '';
      state.filters = {
        createdOn: null,
        atsScore: null,
        atsStatus: 'All',
      };
      state.pagination.page = 1;
    },
    clearResults: state => {
      state.interviewResultDataList = [];
      state.candidateName = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllCandidates.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCandidates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidates = action.payload.candidateList;
        state.pagination.totalRecords = action.payload.totalRecords;
      })
      .addCase(fetchAllCandidates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInterviewResults.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInterviewResults.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interviewResultDataList = action.payload.interviewResultDataList;
        state.candidateName = action.payload.candidateName;
      })
      .addCase(fetchInterviewResults.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSearchQuery,
  setFilters,
  clearFilters,
} = interviewResultsSlice.actions;
export default interviewResultsSlice.reducer;
